import { Button } from "@/components/ui/button";
import { apiClient } from "@/lib/trpc";
import { ArrowLeft, CheckCircle2, CreditCard, Loader2, XCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type CheckoutStatus = Awaited<ReturnType<typeof apiClient.billing.getCheckoutStatus.query>>;

export default function Payments() {
  const [status, setStatus] = useState<CheckoutStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useMemo(() => new URLSearchParams(window.location.search), []);
  const sessionId = search.get("session_id");
  const outcome = search.get("status");
  const cancelledPlan = search.get("plan");

  useEffect(() => {
    if (!sessionId || outcome !== "success") {
      return;
    }

    setLoading(true);
    setError(null);

    void apiClient.billing.getCheckoutStatus
      .query({ sessionId })
      .then(result => {
        setStatus(result);
      })
      .catch(nextError => {
        console.error(nextError);
        setError("The checkout returned to the site, but the payment status could not be loaded yet.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [outcome, sessionId]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <main className="container flex min-h-screen items-center justify-center py-16">
        <section className="w-full max-w-3xl rounded-[2rem] border border-white/10 bg-[rgba(10,12,18,0.82)] p-8 shadow-[0_30px_120px_rgba(0,0,0,0.45)] md:p-10">
          <div className="flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/45">
            <CreditCard className="h-4 w-4 text-[var(--signal-soft)]" />
            Payment status
          </div>

          {outcome === "cancelled" ? (
            <div className="mt-8 space-y-6">
              <XCircle className="h-12 w-12 text-[var(--signal-soft)]" />
              <div>
                <h1 className="font-display text-4xl text-white">Checkout was cancelled.</h1>
                <p className="mt-4 max-w-2xl text-base leading-8 text-white/68">
                  No payment was completed. You can return to the pricing section and restart checkout whenever you are ready.
                </p>
                {cancelledPlan ? (
                  <p className="mt-3 text-sm leading-7 text-white/54">
                    Pending plan: <span className="text-white">{cancelledPlan}</span>
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}

          {outcome !== "cancelled" ? (
            <div className="mt-8 space-y-6">
              {loading ? <Loader2 className="h-12 w-12 animate-spin text-[var(--signal-soft)]" /> : <CheckCircle2 className="h-12 w-12 text-[var(--signal-soft)]" />}
              <div>
                <h1 className="font-display text-4xl text-white">
                  {loading ? "Confirming payment status..." : "Checkout returned successfully."}
                </h1>
                <p className="mt-4 max-w-2xl text-base leading-8 text-white/68">
                  Stripe has returned you to Command Secrets. This page confirms the latest checkout state available to your account.
                </p>
              </div>

              {status ? (
                <div className="grid gap-4 rounded-[1.5rem] border border-white/10 bg-[rgba(255,255,255,0.03)] p-5 md:grid-cols-2">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-white/42">Plan</p>
                    <p className="mt-2 text-lg text-white">{status.planName ?? status.planKey ?? "Command Secrets purchase"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-white/42">Checkout status</p>
                    <p className="mt-2 text-lg text-white">{status.status ?? "unknown"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-white/42">Payment status</p>
                    <p className="mt-2 text-lg text-white">{status.paymentStatus ?? "unknown"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-white/42">Amount</p>
                    <p className="mt-2 text-lg text-white">
                      {typeof status.amountTotal === "number" && status.currency
                        ? `${(status.amountTotal / 100).toFixed(2)} ${status.currency.toUpperCase()}`
                        : "Unavailable"}
                    </p>
                  </div>
                </div>
              ) : null}

              {error ? (
                <div className="rounded-[1.25rem] border border-[rgba(185,57,76,0.28)] bg-[rgba(120,18,31,0.12)] px-4 py-3 text-sm leading-7 text-white/74">
                  {error}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="mt-10 flex flex-wrap gap-3">
            <Button className="bg-[var(--signal)] text-white hover:bg-[var(--signal-strong)]" asChild>
              <a href="/#pricing">Return to pricing</a>
            </Button>
            <Button variant="outline" className="border-white/15 text-white/78 hover:bg-white/8 hover:text-white" asChild>
              <a href="/support">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Contact support
              </a>
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
}
